'use client';

import { useTranslations } from 'next-intl';
import { SectionWrapper } from '@/components/ui/section-wrapper';
import { Typography } from '@/components/ui/typography';
import { SectionHeading } from '@/components/frontend/common/section-heading';
import { CheckCircle2 } from 'lucide-react';
import { useScrollAnimation } from '@/hooks/use-scroll-animation';
import { cn } from '@/lib/utils';

interface BenefitsGridSectionProps {
  translationNamespace: string;
  benefits: string[];
}

export function BenefitsGridSection({ translationNamespace, benefits }: BenefitsGridSectionProps) {
  const t = useTranslations(translationNamespace);
  const sectionRef = useScrollAnimation({ threshold: 0.15 });

  return (
    <SectionWrapper ref={sectionRef.ref} variant="default" padding="md" className="relative bg-white dark:bg-gray-950">
      <div className="container">
        <div className={cn(
          "mb-12 transition-all duration-700",
          sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
        )}>
          <SectionHeading
            page=""
            fallbackTitle={t('title')}
            align="center"
            maxWidthClass="max-w-4xl"
            className="mb-4"
          />
          <Typography variant="p" className="text-center text-gray-600 dark:text-gray-400 max-w-4xl mx-auto leading-relaxed">
            {t('subtitle')}
          </Typography>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {Array.isArray(benefits) && benefits.length > 0 ? benefits.map((benefit, index) => (
            <div
              key={index}
              className={cn(
                "bg-gray-100 dark:bg-gray-800 rounded-xl p-5 flex items-center gap-4 transition-all duration-700",
                sectionRef.isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
              )}
              style={{ transitionDelay: `${200 + index * 50}ms` }}
            >
              <div className="flex-shrink-0 w-10 h-10 rounded-full bg-blue-500 dark:bg-blue-600 flex items-center justify-center">
                <CheckCircle2 className="w-6 h-6 text-white" />
              </div>
              <Typography variant="p" className="text-gray-700 dark:text-gray-300 leading-relaxed flex-1">
                {benefit}
              </Typography>
            </div>
          )) : (
            <div className="col-span-full text-center text-gray-500 dark:text-gray-400 py-8">
              <Typography variant="p">Aucun avantage disponible pour le moment.</Typography>
            </div>
          )}
        </div>
      </div>
    </SectionWrapper>
  );
}
